import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import NumeroInternoMeta from '../../components/NumeroInternoMeta';
import notaCreditoVentaService, {
  NotaCreditoVenta,
  OrdenDesdeNotaPreview,
} from '../../services/notaCreditoVentaService';
import GenerarOrdenDesdeNotaDialog from '../../components/ventas/GenerarOrdenDesdeNotaDialog';
import { facturaPosVerPath } from '../../config/routes';
import { formatCurrency, formatDateLocal, formatClienteConNro } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import { esDocumentoVentaAnulado } from '../../utils/ventasDocumentoAnulacion';
import {
  cantidadDoc,
  labelsExtra,
  showExtraPedFac,
  umBase,
  umDoc,
} from '../../utils/umDocumentoDisplay';

const formatearFecha = (fecha: Date | string | null | undefined): string => {
  if (!fecha) return '-';
  return formatDateLocal(fecha) || '-';
};

const formatoLetraSucNro = (letra?: string, nroSuc?: number, nroComprobante?: number): string => {
  const L = (letra ?? '').trim().toUpperCase() || '-';
  const suc = String(nroSuc ?? 0).padStart(5, '0');
  const nro = String(nroComprobante ?? 0).padStart(8, '0');
  return `${L}-${suc}-${nro}`;
};

const NotaCreditoVentaDetalle: React.FC = () => {
  const navigate = useNavigate();
  const { nroFormulario } = useParams<{ nroFormulario: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [nota, setNota] = useState<NotaCreditoVenta | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewOrden, setPreviewOrden] = useState<OrdenDesdeNotaPreview | null>(null);
  const [ordenNotaId, setOrdenNotaId] = useState<number | null>(null);

  const loadNota = useCallback(async () => {
    const id = parseInt(nroFormulario || '', 10);
    if (Number.isNaN(id) || id <= 0) {
      enqueueSnackbar('Nota no válida', { variant: 'error' });
      navigate('/ventas/notas-credito');
      return;
    }
    try {
      setLoading(true);
      const data = await notaCreditoVentaService.getNotaCreditoVenta(id);
      setNota(data);
      try {
        const preview = await notaCreditoVentaService.getParaOrdenDevolucion(id);
        setPreviewOrden(preview);
      } catch {
        setPreviewOrden(null);
      }
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar la nota de crédito'), {
        variant: 'error',
      });
      navigate('/ventas/notas-credito');
    } finally {
      setLoading(false);
    }
  }, [nroFormulario, enqueueSnackbar, navigate]);

  useEffect(() => {
    void loadNota();
  }, [loadNota]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress sx={{ color: '#1976d2' }} />
      </Box>
    );
  }

  if (!nota) return null;

  const anulada = esDocumentoVentaAnulado(nota.asentado, nota.nombre, true);
  const tieneComprobante =
    Boolean((nota.letra ?? '').trim()) &&
    nota.nroSuc != null &&
    nota.nroComprobante != null;
  const nroDoc = formatoLetraSucNro(nota.letra, nota.nroSuc, nota.nroComprobante ?? undefined);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/ventas/notas-credito')}
              sx={{ textTransform: 'none' }}
            >
              Notas de Crédito
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {tieneComprobante ? `Nota de Crédito ${nroDoc}` : `Nota de Crédito #${nota.nroFormulario}`}
            </Typography>
            {anulada && <Chip label="Anulada" size="small" color="default" variant="outlined" />}
            {!anulada && previewOrden?.puedeGenerar ? (
              <Button variant="contained" onClick={() => setOrdenNotaId(nota.nroFormulario)} sx={{ textTransform: 'none' }}>
                Generar orden de devolución
              </Button>
            ) : null}
            {nota.caeNro ? (
              <Chip label="Con CAE" size="small" sx={{ bgcolor: '#C8E6C9', color: '#2E7D32', fontWeight: 500 }} />
            ) : null}
          </Stack>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Encabezado
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Fecha emisión
              </Typography>
              <Typography variant="body1">{formatearFecha(nota.fechaEmision)}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Nro Comprobante
              </Typography>
              <Typography variant="body1">{nroDoc}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography variant="body1">
                {formatClienteConNro(nota.nroCliente, nota.nombre)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                CUIT
              </Typography>
              <Typography variant="body1">{nota.cuit?.trim() || '-'}</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary">
                Usuario
              </Typography>
              <Typography variant="body1">{nota.usuario || '-'}</Typography>
            </Grid>
            {nota.caeNro ? (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  CAE
                </Typography>
                <Typography variant="body1">{nota.caeNro}</Typography>
              </Grid>
            ) : null}
            {nota.nroFormularioFactura != null && Number(nota.nroFormularioFactura) > 0 && (
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Factura origen
                </Typography>
                <Link
                  component={RouterLink}
                  to={facturaPosVerPath(Number(nota.nroFormularioFactura))}
                  underline="hover"
                  sx={{ fontWeight: 600 }}
                >
                  Nº int. {nota.nroFormularioFactura}
                </Link>
              </Grid>
            )}
            {nota.descripcion ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Descripción
                </Typography>
                <Typography variant="body1">{nota.descripcion}</Typography>
              </Grid>
            ) : null}
          </Grid>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Detalle
          </Typography>
          <TableContainer sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Cantidad
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>UM</TableCell>
                  {(nota.detalles || []).some((d) => showExtraPedFac(d, 'fac')) ? (
                    <>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        {labelsExtra('fac').cant}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{labelsExtra('fac').um}</TableCell>
                    </>
                  ) : null}
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Precio unit.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    % Desc
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Subtotal
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(nota.detalles || []).length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        7 + ((nota.detalles || []).some((d) => showExtraPedFac(d, 'fac')) ? 2 : 0)
                      }
                    >
                      Sin ítems
                    </TableCell>
                  </TableRow>
                ) : (
                  (nota.detalles || []).map((d, idx) => {
                    const showFac = (nota.detalles || []).some((x) => showExtraPedFac(x, 'fac'));
                    return (
                      <TableRow key={`${d.nroItem ?? idx}-${d.codigoArticulo ?? ''}`}>
                        <TableCell>{d.codigoArticulo || '-'}</TableCell>
                        <TableCell>{d.descripcionArticulo || '-'}</TableCell>
                        <TableCell align="right">
                          {Number(d.cantidad)?.toLocaleString('es-AR', {
                            minimumFractionDigits: 2,
                          }) ?? '-'}
                        </TableCell>
                        <TableCell>{umBase(d) || '-'}</TableCell>
                        {showFac ? (
                          <>
                            <TableCell align="right">
                              {showExtraPedFac(d, 'fac')
                                ? (cantidadDoc(d, 'fac') ?? 0).toLocaleString('es-AR', {
                                    minimumFractionDigits: 2,
                                  })
                                : '—'}
                            </TableCell>
                            <TableCell>
                              {showExtraPedFac(d, 'fac') ? umDoc(d, 'fac') || '—' : '—'}
                            </TableCell>
                          </>
                        ) : null}
                        <TableCell align="right">{formatCurrency(d.precioUnitario ?? 0)}</TableCell>
                        <TableCell align="right">
                          {d.porcDesc != null ? `${Number(d.porcDesc).toFixed(1)}%` : '-'}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(d.subtotal ?? 0)}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>
            Totales
          </Typography>
          <Grid container spacing={2} justifyContent="flex-end">
            <Grid item xs={12} md={4}>
              <Stack spacing={1}>
                <Divider sx={{ my: 1 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                  <Typography>Importe total</Typography>
                  <Typography>{formatCurrency(nota.importeTotal ?? 0)}</Typography>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <NumeroInternoMeta nro={nota.nroFormulario} />
        </CardContent>
      </Card>

      <GenerarOrdenDesdeNotaDialog
        notaId={ordenNotaId}
        onClose={() => setOrdenNotaId(null)}
        onCreated={() => {
          void notaCreditoVentaService.getParaOrdenDevolucion(nota.nroFormulario).then(setPreviewOrden).catch(() => setPreviewOrden(null));
        }}
      />
    </Box>
  );
};

export default NotaCreditoVentaDetalle;
